/**
 * All Categories Page
 * Shows all categories as tiles with island-specific banner
 * User selects a category to go to the Category Landing page
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import axios from '../lib/axios';
import { API, useLanguage } from '../App';
import { useIsland } from '../contexts/IslandContext';
import { getIconComponent } from '../utils/categoryIcons';
import Navbar from '../components/Navbar';
import MobileBottomNav from '../components/MobileBottomNav';
import Footer from '../components/Footer';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';

// Island-specific banner text (EN/NL/ES)
const ISLAND_BANNER_TEXT = {
  aruba: {
    en: { headline: 'Explore Aruba', description: '' },
    nl: { headline: 'Ontdek Aruba', description: '' },
    es: { headline: 'Explora Aruba', description: '' }
  },
  bonaire: {
    en: { headline: 'Explore Bonaire', description: '' },
    nl: { headline: 'Ontdek Bonaire', description: '' },
    es: { headline: 'Explora Bonaire', description: '' }
  },
  curacao: {
    en: { headline: 'Explore Curaçao', description: '' },
    nl: { headline: 'Ontdek Curaçao', description: '' },
    es: { headline: 'Explora Curazao', description: '' }
  }
};

// Island flags
const ISLAND_FLAGS = {
  aruba: '🇦🇼',
  bonaire: '🇧🇶',
  curacao: '🇨🇼'
};

// Get banner text
const getBannerText = (island, language) => {
  const islandKey = island?.toLowerCase() || 'curacao';
  const langKey = language?.toLowerCase() || 'en';
  const texts = ISLAND_BANNER_TEXT[islandKey] || ISLAND_BANNER_TEXT.all;
  return texts[langKey] || texts.en;
};

// Island Banner Component
const IslandBanner = () => {
  const { currentIsland } = useIsland();
  const { language } = useLanguage();
  const bannerText = getBannerText(currentIsland, language);
  const flag = ISLAND_FLAGS[currentIsland?.toLowerCase()] || ISLAND_FLAGS.all;
  
  return (
    <div 
      className="relative w-full overflow-hidden rounded-xl mb-4 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border border-primary/20 shadow-lg"
      data-testid="island-banner"
    >
      <div className="aspect-[6/1] relative flex items-center">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
          }} />
        </div>
        
        {/* Content */}
        <div className="relative px-5 py-4 md:px-8 flex items-center gap-4 w-full">
          <div className="text-4xl md:text-5xl">{flag}</div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-foreground">
              {bannerText.headline}
            </h2>
            {bannerText.description && (
              <p className="text-xs md:text-sm text-muted-foreground">
                {bannerText.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Category Card Component - Clean brand-focused design with background image
const CategoryCard = ({ category, onClick }) => {
  const Icon = getIconComponent(category.icon);
  
  return (
    <button
      onClick={() => onClick(category)}
      className="bg-card border border-border rounded-2xl overflow-hidden group cursor-pointer hover:border-primary/40 shadow-md dark:shadow-none hover:shadow-xl transition-all duration-200 flex flex-col text-left"
      data-testid={`category-card-${category.id}`}
    >
      {/* Image Container with overlay */}
      <div className="aspect-[4/3] relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-secondary to-secondary/50">
        {/* Background Image - Full Color */}
        {category.image_url && (
          <img 
            src={category.image_url} 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover opacity-60 saturate-150 group-hover:opacity-100 group-hover:saturate-100 group-hover:scale-110 transition-all duration-500"
          />
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
        
        {/* Icon Box - Light/Dark optimized */}
        <div className="relative z-10 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 dark:bg-white/20 backdrop-blur-md border border-primary/20 dark:border-white/30 shadow-lg flex items-center justify-center group-hover:scale-105 transition-all duration-200">
          <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-primary dark:text-white dark:drop-shadow-md" />
        </div>
      </div>
      
      {/* Content Area */}
      <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-foreground text-sm sm:text-base font-semibold truncate">
            {category.name}
          </h3>
          {category.subcategories?.length > 0 && (
            <p className="text-muted-foreground text-xs">
              {category.subcategories.length} subcategories
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>
    </button>
  );
};

// Skeleton Card - Clean Design
const CategoryCardSkeleton = () => (
  <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col shadow-md dark:shadow-none">
    <div className="aspect-[4/3] bg-secondary/30 flex items-center justify-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 dark:bg-white/20 animate-pulse" />
    </div>
    <div className="p-3 sm:p-4">
      <div className="h-4 sm:h-5 w-20 sm:w-24 bg-muted rounded mb-1.5" />
      <div className="h-3 w-14 sm:w-16 bg-muted/70 rounded" />
    </div>
  </div>
);

// CategoriesBreadcrumbs - Uses reusable Breadcrumbs component with fixed-height wrapper
const CategoriesBreadcrumbs = () => {
  const { language } = useLanguage();
  
  const items = [
    { label: 'Home', href: '/' },
    { label: language === 'nl' ? 'Categorieën' : language === 'es' ? 'Categorías' : 'Categories' }
  ];
  
  return (
    <div className="hidden md:flex h-10 items-center mb-4" data-testid="categories-breadcrumbs-wrapper">
      <Breadcrumbs 
        items={items}
        data-testid="categories-breadcrumbs"
      />
    </div>
  );
};

// Main Component
const CategoriesPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { currentIsland } = useIsland();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories?lang=${language}`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, [language]);
  
  // Handle category click - navigate to marketplace with category filter
  const handleCategoryClick = (category) => {
    // Navigate to marketplace with category (this will show CategoryLanding)
    const params = new URLSearchParams();
    params.set('category', category.id);
    navigate(`/marketplace?${params.toString()}`);
  };
  
  // Page title based on language
  const pageTitle = language === 'nl' ? 'Alle Categorieën' : language === 'es' ? 'Todas las Categorías' : 'All Categories';
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumbs - Fixed height wrapper */}
        <CategoriesBreadcrumbs />
        
        {/* Island Banner */}
        <IslandBanner />
        
        {/* Page Title - Matching CategoryLanding style */}
        <h2 className="text-xl font-bold text-foreground mt-6 mb-4">
          {pageTitle}
        </h2>
        
        {/* Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <CategoryCardSkeleton key={`skeleton-${i}`} />
            ))
          ) : (
            categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                onClick={handleCategoryClick}
              />
            ))
          )}
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CategoriesPage;
